import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail, formatDateTime } from "@/lib/api";
import { Loader2, Plus, X, Save } from "lucide-react";
import { toast } from "sonner";

export default function SheetTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingIdx, setSavingIdx] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [stoppingSync, setStoppingSync] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [saJson, setSaJson] = useState("");
  const [constants, setConstants] = useState({ sources: [], organizations: [] });
  const [sourceTests, setSourceTests] = useState({});

  const load = () => api.get("/admin/sheet/config").then(r => setCfg(r.data));
  const loadConstants = () => api.get("/admin/constants").then(r => setConstants({
    sources: r.data.sources || [],
    organizations: r.data.organizations || [],
  }));
  const loadHistory = () => api.get("/admin/sheet/sync/history?limit=20").then(r => setHistory(r.data || []));
  const loadStatus = () => api.get("/admin/sheet/sync/status").then(r => {
    setSyncStatus(r.data);
    setSyncing(!!r.data.running);
    return r.data;
  });
  useEffect(() => { load(); }, []);
  useEffect(() => { loadConstants().catch(() => {}); }, []);
  useEffect(() => { loadStatus().catch(() => {}); }, []);
  useEffect(() => { loadHistory().catch(() => {}); }, []);
  useEffect(() => {
    const refreshConstants = () => loadConstants().catch(() => {});
    window.addEventListener("kaya:constants-updated", refreshConstants);
    return () => window.removeEventListener("kaya:constants-updated", refreshConstants);
  }, []);
  useEffect(() => {
    if (!syncing) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const status = await loadStatus();
        if (!active || status.running) return;
        await Promise.all([load(), loadHistory()]);
        if (status.status === "completed") {
          toast.success(`Sheet sync completed - ${status.result?.matched || 0} donation(s) verified`);
        } else if (status.status === "failed" && status.error) {
          toast.error(status.error);
        }
      } catch (_) {}
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [syncing]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cfg) return <div className="text-[#5C635E]"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const sources = cfg.sources || [];
  const autoSyncAnchor = cfg.last_auto_sync_at || cfg.auto_sync_configured_at;
  const autoSyncAnchorMs = Date.parse(autoSyncAnchor || "");
  const nextAutoSyncAt = Number.isFinite(autoSyncAnchorMs)
    ? new Date(autoSyncAnchorMs + Number(cfg.sync_interval_hours || 24) * 60 * 60 * 1000).toISOString()
    : null;

  const updateSource = (i, patch) => {
    const next = sources.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    setCfg({ ...cfg, sources: next });
  };
  const addSource = () => {
    setCfg({ ...cfg, sources: [...sources, { sheet_id: "", tab_name: "", organizations: [], sources: [], phone_column_header: "Mobile No", name_column_header: "", header_row: 1, transaction_column_header: "" }] });
  };
  const removeSource = (i) => {
    setCfg({ ...cfg, sources: sources.filter((_, idx) => idx !== i) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sources: (cfg.sources || []).map(s => ({
          sheet_id: (s.sheet_id || "").trim(),
          tab_name: (s.tab_name || "").trim(),
          organizations: cleanList(s.organizations),
          sources: cleanList(s.sources),
          phone_column_header: (s.phone_column_header || "Mobile No").trim(),
          name_column_header: (s.name_column_header || "").trim(),
          header_row: Math.max(1, Number(s.header_row) || 1),
          date_column_header: (s.date_column_header || "").trim(),
          amount_column_header: (s.amount_column_header || "").trim(),
          transaction_column_header: (s.transaction_column_header || "").trim(),
        })),
        sync_interval_hours: Math.max(1, Number(cfg.sync_interval_hours || 24)),
        enabled: !!cfg.enabled,
        name_verification_enabled: !!cfg.name_verification_enabled,
      };
      if (saJson.trim()) payload.service_account_json = saJson.trim();
      await api.put("/admin/sheet/config", payload);
      toast.success("Saved");
      setSaJson("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const saveSource = async (i) => {
    setSavingIdx(i);
    const label = (sources[i]?.tab_name || `source #${i + 1}`).trim() || `source #${i + 1}`;
    try {
      const payload = {
        sources: (cfg.sources || []).map(s => ({
          sheet_id: (s.sheet_id || "").trim(),
          tab_name: (s.tab_name || "").trim(),
          organizations: cleanList(s.organizations),
          sources: cleanList(s.sources),
          phone_column_header: (s.phone_column_header || "Mobile No").trim(),
          name_column_header: (s.name_column_header || "").trim(),
          header_row: Math.max(1, Number(s.header_row) || 1),
          date_column_header: (s.date_column_header || "").trim(),
          amount_column_header: (s.amount_column_header || "").trim(),
          transaction_column_header: (s.transaction_column_header || "").trim(),
        })),
      };
      await api.put("/admin/sheet/config", payload);
      toast.success(`Saved ${label}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSavingIdx(null); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sheet/sync");
      if (data.success && data.started) toast.success("Sheet sync started. You can keep using the app.");
      else if (data.success && data.running) toast.success("Sheet sync is already running.");
      else toast.error(data.error || "Sync failed");
      await loadStatus();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const stopStaleSync = async () => {
    if (stoppingSync) return;
    setStoppingSync(true);
    try {
      const { data } = await api.post("/admin/sheet/sync/stop", null, { timeout: 15000 });
      toast.success(data.message || "Stale Sheet sync stopped");
      await Promise.all([loadStatus(), loadHistory(), load()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await loadStatus().catch(() => {});
    } finally { setStoppingSync(false); }
  };

  const testSource = async (i) => {
    setSourceTests(current => ({ ...current, [i]: { loading: true } }));
    try {
      const { data } = await api.post("/admin/sheet/test-source", sources[i]);
      setSourceTests(current => ({ ...current, [i]: { ...data, loading: false } }));
      toast.success(`Connected to ${data.tab_name}. ${data.headers.length} header(s) detected.`);
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      setSourceTests(current => ({ ...current, [i]: { loading: false, error: message } }));
      toast.error(message);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-sheet">
      <div>
        <div className="text-overline">Verification</div>
        <h2 className="text-2xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Google Sheet Sync</h2>
        <p className="text-sm text-[#5C635E] mt-1">
          Configure one or more Google Sheet tabs. Dates are detected automatically; sync only processes dates that currently have pending donations.
        </p>
      </div>

      <div className="card-warm p-6 space-y-4" data-testid="sheet-sources">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Sheet sources ({sources.length})</h3>
          <button data-testid="sheet-add-source" onClick={addSource}
            className="text-sm text-[#4A5D4E] hover:underline flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add source
          </button>
        </div>

        {sources.length === 0 && (
          <div className="text-sm text-[#8A938D]">No sources configured. Click &ldquo;Add source&rdquo; to add one.</div>
        )}

        {sources.map((s, i) => {
          const perSrc = (cfg.last_sync_per_source || []).find(r => r.index === i) || null;
          return (
            <div key={i} data-testid={`sheet-source-${i}`} className="border border-[#EAE6DF] rounded-xl p-4 bg-[#FDFBF7]">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-[#2D312E]">
                  Source #{i + 1} {s.tab_name && <span className="text-[#8A938D] font-normal">· {s.tab_name}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => testSource(i)} disabled={sourceTests[i]?.loading}
                    className="border border-[#D8D4CC] rounded-lg px-3 py-1.5 text-xs disabled:opacity-50">
                    {sourceTests[i]?.loading ? "Testing…" : "Test connection"}
                  </button>
                  <button data-testid={`sheet-save-source-${i}`} onClick={() => saveSource(i)}
                    disabled={savingIdx === i}
                    className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-lg px-3 py-1.5 text-xs flex items-center gap-1">
                    <Save className="h-3.5 w-3.5" />
                    {savingIdx === i ? "Saving…" : "Save"}
                  </button>
                  <button data-testid={`sheet-remove-source-${i}`} onClick={() => removeSource(i)}
                    className="text-[#C05746] text-sm hover:underline flex items-center gap-1">
                    <X className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Sheet ID</label>
                  <input data-testid={`sheet-source-${i}-sheet-id`} value={s.sheet_id || ""}
                    placeholder="From the sheet URL /d/<this-part>/edit"
                    onChange={e => updateSource(i, { sheet_id: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Tab name</label>
                  <input data-testid={`sheet-source-${i}-tab-name`} value={s.tab_name || ""}
                    placeholder="e.g. ZMS EB UPI"
                    onChange={e => updateSource(i, { tab_name: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
                <MultiPick
                  label="Organizations for this sheet"
                  hint="Blank means all organizations"
                  values={cleanList(s.organizations)}
                  options={constants.organizations}
                  onChange={(values) => updateSource(i, { organizations: values })}
                  testid={`sheet-source-${i}-orgs`}
                />
                <MultiPick
                  label="Sources for this sheet"
                  hint="Blank means all sources"
                  values={cleanList(s.sources)}
                  options={constants.sources}
                  onChange={(values) => updateSource(i, { sources: values })}
                  testid={`sheet-source-${i}-sources`}
                />
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Phone column header</label>
                  <input data-testid={`sheet-source-${i}-phone-col`} value={s.phone_column_header || ""}
                    placeholder="Mobile No"
                    onChange={e => updateSource(i, { phone_column_header: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Donor name column header <span className="text-[#8A938D] font-normal">(optional fallback)</span></label>
                  <input data-testid={`sheet-source-${i}-name-col`} value={s.name_column_header || ""}
                    placeholder="e.g. Name / Donors Name"
                    onChange={e => updateSource(i, { name_column_header: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Header row (row # where column headers sit)</label>
                  <input data-testid={`sheet-source-${i}-header-row`} type="number" min="1"
                    value={Number(s.header_row) || 1}
                    onChange={e => updateSource(i, { header_row: Math.max(1, Number(e.target.value) || 1) })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Date column header*</label>
                  <input data-testid={`sheet-source-${i}-date-col`} value={s.date_column_header || ""}
                    placeholder="e.g. Date"
                    onChange={e => updateSource(i, { date_column_header: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Amount column header <span className="text-[#8A938D] font-normal">(optional)</span></label>
                  <input data-testid={`sheet-source-${i}-amount-col`} value={s.amount_column_header || ""}
                    placeholder="e.g. Amount  (leave empty to skip amount match)"
                    onChange={e => updateSource(i, { amount_column_header: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#5C635E] mb-1 block">Transaction column header <span className="text-[#8A938D] font-normal">(optional)</span></label>
                  <input data-testid={`sheet-source-${i}-txn-col`} value={s.transaction_column_header || ""}
                    placeholder="e.g. UTR / Transaction ID"
                    onChange={e => updateSource(i, { transaction_column_header: e.target.value })}
                    className="w-full bg-white border border-[#EAE6DF] rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="mt-3 text-[11px] text-[#8A938D] leading-relaxed">
                Match rule: only Sheet rows whose date equals a pending donation date are processed. Organization/source filters are applied first. Transaction ID is used when available; otherwise matching uses <b>phone (last 10 digits)</b>, exact date{s.amount_column_header ? ", and amount" : ""}.{cfg.name_verification_enabled && s.name_column_header ? " If the phone differs, the complete donor name, date and amount must match exactly." : ""}
              </div>
              {sourceTests[i]?.error && <div className="mt-3 text-xs text-[#C05746]">Connection failed: {sourceTests[i].error}</div>}
              {sourceTests[i]?.success && <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                Connected · {sourceTests[i].row_count ?? "?"} rows · Headers: {(sourceTests[i].headers || []).filter(Boolean).join(", ") || "none"}
              </div>}
              {perSrc && (
                <div className="mt-3 text-xs text-[#5C635E] flex flex-wrap gap-3" data-testid={`sheet-source-${i}-last-result`}>
                  <span>Last sync: <span className="font-semibold text-[#4A5D4E]">{perSrc.matched} matched</span> from {perSrc.rows} matching-date rows</span>
                  {perSrc.error && <span className="text-[#C05746]">Error: {perSrc.error}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card-warm p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Run auto-sync every</label>
          <select data-testid="sheet-sync-interval-hours" value={cfg.sync_interval_hours ?? 24}
            onChange={e => setCfg({ ...cfg, sync_interval_hours: Number(e.target.value) })}
            className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2.5">
            {Array.from({ length: 24 }, (_, index) => index + 1).map((hours) => (
              <option key={hours} value={hours}>{hours} hour{hours === 1 ? "" : "s"}</option>
            ))}
          </select>
          <p className="text-xs text-[#8A938D] mt-1.5">The timer starts when this setting is saved. Manual sync does not reset it.</p>
          {cfg.enabled && nextAutoSyncAt && <p className="text-xs font-medium text-[#4A5D4E] mt-1">Next automatic sync: {formatDateTime(nextAutoSyncAt)}</p>}
        </div>
        <div className="md:col-span-2 flex items-center gap-3">
          <label className="text-sm text-[#5C635E]">Enabled:</label>
          <input data-testid="sheet-enabled" type="checkbox" checked={!!cfg.enabled}
            onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
        </div>
        <label className="md:col-span-2 flex items-start gap-3 rounded-xl border border-[#EAE6DF] bg-[#FDFBF7] p-4 cursor-pointer">
          <input data-testid="sheet-name-verification-enabled" type="checkbox"
            checked={!!cfg.name_verification_enabled}
            onChange={e => setCfg({ ...cfg, name_verification_enabled: e.target.checked })}
            className="mt-1 h-4 w-4" />
          <span>
            <span className="block text-sm font-medium text-[#2D312E]">Allow exact full-name fallback</span>
            <span className="block text-xs text-[#8A938D] mt-1">When the phone does not match, verify only when the complete normalized donor name, donation date and amount all match. Configure the donor-name header for each applicable source.</span>
          </span>
        </label>
        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">
            Service Account JSON {cfg.service_account_configured && <span className="text-emerald-700 text-xs">(configured — paste again to replace)</span>}
          </label>
          <textarea data-testid="sheet-sa-json" value={saJson} onChange={e => setSaJson(e.target.value)} rows={6}
            placeholder='Paste the full JSON of your Google service account…'
            className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-2.5 font-mono text-xs" />
          <p className="text-xs text-[#8A938D] mt-1">Share every configured sheet with the service account email (the <code>client_email</code> inside the JSON).</p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="sheet-save" onClick={() => save()} disabled={saving}
          className="bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 text-white rounded-xl px-6 py-2.5 text-sm">
          {saving ? "Saving…" : "Save all"}
        </button>
        <button data-testid="sheet-sync-now" onClick={syncNow} disabled={syncing}
          className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-xl px-6 py-2.5 text-sm text-[#2D312E]">
          {syncing ? "Sync running..." : "Sync now"}
        </button>
        {syncStatus?.running && syncStatus?.can_stop && (
          <button type="button" data-testid="sheet-stop-stale-sync" onClick={stopStaleSync} disabled={stoppingSync || syncStatus.stop_requested}
            className="border border-[#C05746] bg-red-50 hover:bg-red-100 disabled:opacity-60 text-[#A9483A] rounded-xl px-5 py-2.5 text-sm">
            {stoppingSync || syncStatus.stop_requested ? "Stopping…" : "Stop sync"}
          </button>
        )}
        {syncStatus?.running && (
          <div className="text-xs text-[#4A5D4E]" data-testid="sheet-sync-running">
            Sync started {syncStatus.started_at ? formatDateTime(syncStatus.started_at) : ""}. 
            {syncStatus.stop_requested ? "Stopping after the current Sheet request…" : syncStatus.stale ? "This sync is stale and can be stopped." : "This can take a few minutes for large sheets."}
          </div>
        )}
        {cfg.last_sync_at && (
          <div className="text-xs text-[#8A938D]" data-testid="sheet-last-sync">
            Last sync: {formatDateTime(cfg.last_sync_at)} · matched {cfg.last_sync_matched || 0} across {(cfg.last_sync_per_source || []).length} sources
          </div>
        )}
      </div>

      {(syncStatus?.logs || []).length > 0 && (
        <div className="card-warm overflow-hidden" data-testid="sheet-sync-live-log">
          <div className="p-4 border-b border-[#EAE6DF] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-overline">Live diagnostics</div>
              <h3 className="text-lg font-medium text-[#2D312E]">Sync progress log</h3>
            </div>
            <div className="text-xs text-[#5C635E]">
              Current step: <span className="font-semibold">{humanizeStage(syncStatus.stage)}</span>
              {syncStatus.updated_at && <> · updated {formatDateTime(syncStatus.updated_at)}</>}
            </div>
          </div>
          <ol className="max-h-72 overflow-y-auto divide-y divide-[#EAE6DF]" aria-label="Sheet sync progress log">
            {[...(syncStatus.logs || [])].reverse().map((entry, index) => (
              <li key={`${entry.at}-${index}`} className="px-4 py-3 flex items-start gap-3 text-sm">
                <span className="shrink-0 rounded-full border border-[#D8D4CC] bg-[#F4F1EA] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#5C635E]">
                  {humanizeStage(entry.stage)}
                </span>
                <span className="flex-1 text-[#2D312E]">{entry.message}</span>
                <time className="shrink-0 text-xs text-[#8A938D]" dateTime={entry.at}>{entry.at ? formatDateTime(entry.at) : ""}</time>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="card-warm p-5 space-y-3" data-testid="sheet-sync-history">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-overline">Diagnostics</div>
            <h3 className="text-lg font-medium text-[#2D312E]" style={{ fontFamily: "Manrope" }}>Sync history</h3>
          </div>
          <button type="button" onClick={loadHistory} className="border border-[#EAE6DF] hover:border-[#4A5D4E] rounded-lg px-3 py-1.5 text-xs text-[#2D312E]">
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="bg-[#F4F1EA] text-[#5C635E] text-xs uppercase tracking-wider">
                <th className="text-left p-3">Started</th>
                <th className="text-left p-3">Result</th>
                <th className="text-right p-3">Matching-date rows</th>
                <th className="text-right p-3">Matched</th>
                <th className="text-right p-3">Duration</th>
                <th className="text-left p-3">Errors</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-[#8A938D]">No sync runs yet.</td></tr>
              ) : history.map((run) => (
                <tr key={run.id} className="border-t border-[#EAE6DF]">
                  <td className="p-3 text-[#5C635E]">{run.started_at ? formatDateTime(run.started_at) : "-"}</td>
                  <td className="p-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${run.success ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                      {run.success ? "Success" : "Failed"}
                    </span>
                  </td>
                  <td className="p-3 text-right text-[#5C635E]">{run.rows_scanned || 0}</td>
                  <td className="p-3 text-right font-semibold text-[#4A5D4E]">{run.matched || 0}</td>
                  <td className="p-3 text-right text-[#5C635E]">{Math.round((run.duration_ms || 0) / 1000)}s</td>
                  <td className="p-3 text-[#C05746] text-xs">{run.error || (run.error_count ? `${run.error_count} source error(s)` : "-")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function humanizeStage(value) {
  if (!value) return "Idle";
  return String(value).replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function cleanList(values) {
  if (!values) return [];
  const list = Array.isArray(values) ? values : String(values).split(",");
  return list
    .map((v) => String(v || "").trim().toLowerCase())
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i);
}

function MultiPick({ label, hint, values, options, onChange, testid }) {
  const cleanOptions = cleanList(options);
  const toggle = (value) => {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  };
  return (
    <div data-testid={testid}>
      <label className="text-xs font-medium text-[#5C635E] mb-1 block">{label}</label>
      <div className="border border-[#EAE6DF] rounded-lg bg-white px-3 py-2 min-h-[42px]">
        {cleanOptions.length === 0 ? (
          <div className="text-xs text-[#8A938D]">{hint}</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {cleanOptions.map((opt) => (
              <label key={opt} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs cursor-pointer ${values.includes(opt) ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-[#FDFBF7] text-[#5C635E] border-[#EAE6DF]"}`}>
                <input type="checkbox" className="sr-only" checked={values.includes(opt)} onChange={() => toggle(opt)} />
                {opt.toUpperCase()}
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="text-[10px] text-[#8A938D] mt-1">{hint}</div>
    </div>
  );
}
